import React, { useState, useCallback } from 'react';
import { Box, TextField, Typography, InputAdornment, IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import NumericKeypad from './NumericKeypad';

const TITLE =
  'If you would like to make an initial payment, enter the amount here.';

function formatMoney2(n) {
  const x = Number(n);
  if (!Number.isFinite(x)) return '';
  return (Math.round(x * 100) / 100).toFixed(2);
}

function appendMoneyKey(current, ch) {
  if (ch === '.') {
    if (String(current).includes('.')) return String(current);
    if (!current || current === '') return '0.';
    return String(current) + '.';
  }
  if (!/^\d$/.test(ch)) return String(current);
  const next = String(current || '') + ch;
  if (!/^\d*(\.\d{0,2})?$/.test(next)) return String(current);
  return next;
}

/**
 * Read-only amount field: tap/focus opens bottom mini NumericKeypad (with decimal).
 * Commits to parent on Done (clamping is done in updateItemPayLineTotal).
 */
export default function PartialLinePayField({
  itemId,
  lineList,
  minPay,
  value,
  payLineCleared = false,
  onCommit,
  onClear,
  sx = {},
}) {
  const [keypadOpen, setKeypadOpen] = useState(false);
  const [draft, setDraft] = useState('');

  const isUnset = payLineCleared && (value == null || value === '');

  // Show full line when not yet set; user can change to any amount from min to full.
  const amountShown =
    isUnset
      ? null
      : (value != null && value !== '' ? Number(value) : lineList);

  const displayValue = keypadOpen
    ? draft
    : (isUnset
      ? ''
      : (amountShown != null && Number.isFinite(amountShown)
        ? formatMoney2(amountShown)
        : formatMoney2(lineList)));

  const openKeypad = useCallback(
    (e) => {
      if (e) {
        e.preventDefault();
        e.stopPropagation();
      }
      if (isUnset) {
        setDraft('');
      } else {
        setDraft(
          formatMoney2(
            value != null && value !== '' ? value : lineList
          ).replace(/,/g, '')
        );
      }
      setKeypadOpen(true);
    },
    [value, lineList, isUnset]
  );

  const handleKeypadInput = useCallback(
    (ch) => {
      setDraft((prev) => {
        const next = appendMoneyKey(prev, ch);
        if (next && next !== '.' && next.endsWith('.')) return next;
        const n = parseFloat(next);
        if (Number.isFinite(n) && n > lineList) {
          return (Math.round(lineList * 100) / 100).toFixed(2);
        }
        return next;
      });
    },
    [lineList]
  );

  const handleKeypadBackspace = useCallback(() => {
    setDraft((prev) => String(prev).slice(0, -1));
  }, []);

  const handleKeypadClear = useCallback(() => {
    setDraft(formatMoney2(lineList).replace(/,/g, ''));
  }, [lineList]);

  const handleKeypadDone = useCallback(() => {
    setKeypadOpen(false);
    const t = String(draft).trim();
    if (t === '' || t === '.') {
      if (payLineCleared) {
        if (typeof onClear === 'function') onClear(itemId);
      } else {
        onCommit(itemId, lineList);
      }
      return;
    }
    const n = parseFloat(t);
    if (!Number.isFinite(n)) {
      if (payLineCleared && typeof onClear === 'function') {
        onClear(itemId);
      } else {
        onCommit(itemId, lineList);
      }
      return;
    }
    onCommit(itemId, n);
  }, [draft, itemId, lineList, onCommit, onClear, payLineCleared]);

  const handleFieldClear = useCallback(
    (e) => {
      e.preventDefault();
      e.stopPropagation();
      setKeypadOpen(false);
      if (typeof onClear === 'function') onClear(itemId);
    },
    [itemId, onClear]
  );

  const helperText = isUnset
    ? `Enter an amount. Minimum: $${minPay.toFixed(2)}.`
    : `Minimum: $${minPay.toFixed(2)}.`;

  return (
    <Box sx={{ mt: 1.5, ...sx }}>
      <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.75, lineHeight: 1.4 }}>
        {TITLE}
      </Typography>
      <TextField
        type="text"
        inputMode="none"
        autoComplete="off"
        name={`partial-line-pay-${String(itemId)}`}
        size="small"
        fullWidth
        placeholder="Tap to enter amount"
        value={displayValue}
        onChange={() => {}}
        onClick={openKeypad}
        onFocus={openKeypad}
        inputProps={{
          readOnly: true,
          'aria-label': TITLE,
        }}
        helperText={helperText}
        FormHelperTextProps={{ sx: { lineHeight: 1.4 } }}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <IconButton
                size="small"
                aria-label="Clear amount and enter manually"
                edge="end"
                onClick={handleFieldClear}
                sx={{ color: 'text.secondary' }}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </InputAdornment>
          )
        }}
      />
      <NumericKeypad
        open={keypadOpen}
        allowDecimal
        onInput={handleKeypadInput}
        onBackspace={handleKeypadBackspace}
        onClear={handleKeypadClear}
        onDone={handleKeypadDone}
      />
    </Box>
  );
}
